import React from 'react';
import {
  Box,
  Heading,
  Text,
  Badge,
  Flex,
  Code,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';

const methodColors = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  DELETE: 'red',
};

const ApiDocs = () => {
  const applicationEndpoints = [
    { 
      title: 'List all applications', 
      method: 'GET', 
      path: '/api/applications/', 
      description: 'Retrieve a list of all registered applications.',
      response: `[
  {
    "id": 1,
    "name": "my-application",
    "description": "My awesome application",
    "repository_url": "https://github.com/org/repo",
    "documentation_url": "https://docs.example.com",
    "is_active": true,
    "version_count": 5,
    "latest_version": {"version": "1.2.0", "created_at": "2024-01-15T10:00:00Z"},
    "created_at": "2024-01-01T00:00:00Z",
    "updated_at": "2024-01-15T10:00:00Z"
  }
]`
    },
    { 
      title: 'Get application details', 
      method: 'GET', 
      path: '/api/applications/:id/', 
      description: 'Retrieve details for a specific application by ID, including all versions.',
      response: `{
  "id": 1,
  "name": "my-application",
  "description": "My awesome application",
  "repository_url": "https://github.com/org/repo",
  "documentation_url": "https://docs.example.com",
  "is_active": true,
  "version_count": 5,
  "latest_version": {"version": "1.2.0", "created_at": "2024-01-15T10:00:00Z"},
  "versions": [
    {"id": 1, "version": "1.0.0", "description": "Initial release", "is_stable": true},
    {"id": 2, "version": "1.1.0", "description": "Bug fixes", "is_stable": true},
    {"id": 3, "version": "1.2.0", "description": "New features", "is_stable": true}
  ],
  "created_at": "2024-01-01T00:00:00Z",
  "updated_at": "2024-01-15T10:00:00Z"
}`
    },
    { 
      title: 'Create application', 
      method: 'POST', 
      path: '/api/applications/', 
      description: 'Create a new application registration.',
      request: `{
  "name": "new-application",
  "description": "A new application",
  "repository_url": "https://github.com/org/new-repo",
  "documentation_url": "https://docs.example.com/new",
  "is_active": true
}`,
      response: `{
  "id": 2,
  "name": "new-application",
  "description": "A new application",
  "repository_url": "https://github.com/org/new-repo",
  "documentation_url": "https://docs.example.com/new",
  "is_active": true,
  "version_count": 0,
  "latest_version": null,
  "created_at": "2024-01-16T00:00:00Z",
  "updated_at": "2024-01-16T00:00:00Z"
}`
    },
    { 
      title: 'Update application', 
      method: 'PUT', 
      path: '/api/applications/:id/', 
      description: 'Update an existing application by ID.',
      request: `{
  "name": "updated-application",
  "description": "Updated description",
  "is_active": false
}`,
      response: `{
  "id": 1,
  "name": "updated-application",
  "description": "Updated description",
  "is_active": false,
  ...
}`
    },
    { 
      title: 'Delete application', 
      method: 'DELETE', 
      path: '/api/applications/:id/', 
      description: 'Permanently delete an application and all its versions.',
      response: `204 No Content`
    },
  ];

  const versionEndpoints = [
    { 
      title: 'List versions for application', 
      method: 'GET', 
      path: '/api/applications/:id/versions/', 
      description: 'Retrieve all versions for a specific application.',
      response: `[
  {
    "id": 1,
    "version": "1.0.0",
    "description": "Initial release",
    "release_notes": "First stable release",
    "is_stable": true,
    "is_deprecated": false,
    "artifact_url": "https://artifacts.example.com/v1.0.0.zip",
    "commit_sha": "abc123def456789",
    "created_at": "2024-01-01T00:00:00Z",
    "updated_at": "2024-01-01T00:00:00Z"
  }
]`
    },
    { 
      title: 'Create version', 
      method: 'POST', 
      path: '/api/applications/:id/versions/', 
      description: 'Register a new version for an application.',
      request: `{
  "version": "1.3.0",
  "description": "New feature release",
  "release_notes": "- Added feature X\\n- Fixed bug Y",
  "is_stable": true,
  "is_deprecated": false,
  "artifact_url": "https://artifacts.example.com/v1.3.0.zip",
  "commit_sha": "def456abc123789"
}`,
      response: `{
  "id": 4,
  "version": "1.3.0",
  "description": "New feature release",
  "release_notes": "- Added feature X\\n- Fixed bug Y",
  "is_stable": true,
  "is_deprecated": false,
  "artifact_url": "https://artifacts.example.com/v1.3.0.zip",
  "commit_sha": "def456abc123789",
  "created_at": "2024-01-17T00:00:00Z",
  "updated_at": "2024-01-17T00:00:00Z"
}`
    },
    { 
      title: 'Update version', 
      method: 'PUT', 
      path: '/api/versions/:id/', 
      description: 'Update an existing version.',
      request: `{
  "is_deprecated": true,
  "release_notes": "This version is deprecated, please upgrade to 2.0.0"
}`,
      response: `{
  "id": 1,
  "version": "1.0.0",
  "is_deprecated": true,
  ...
}`
    },
    { 
      title: 'Delete version', 
      method: 'DELETE', 
      path: '/api/versions/:id/', 
      description: 'Delete a version.',
      response: `204 No Content`
    },
  ];

  const apiKeyEndpoints = [
    { 
      title: 'List API keys', 
      method: 'GET', 
      path: '/api/api-keys/', 
      description: 'Retrieve all API keys.',
      response: `[
  {
    "id": 1,
    "name": "Production API Key",
    "key": "abc123-def456-ghi789",
    "description": "Used by CI/CD pipeline",
    "is_active": true,
    "created_at": "2024-01-01T00:00:00Z",
    "updated_at": "2024-01-01T00:00:00Z"
  }
]`
    },
    { 
      title: 'Create API key', 
      method: 'POST', 
      path: '/api/api-keys/', 
      description: 'Create a new API key. A UUID will be automatically generated.',
      request: `{
  "name": "New API Key",
  "description": "For external service",
  "is_active": true
}`,
      response: `{
  "id": 2,
  "name": "New API Key",
  "key": "generated-uuid-here",
  "description": "For external service",
  "is_active": true,
  "created_at": "2024-01-17T00:00:00Z",
  "updated_at": "2024-01-17T00:00:00Z"
}`
    },
    { 
      title: 'Delete API key', 
      method: 'DELETE', 
      path: '/api/api-keys/:id/', 
      description: 'Revoke and delete an API key.',
      response: `204 No Content`
    },
  ];

  const legacyEndpoints = [
    { 
      title: 'Ping', 
      method: 'GET', 
      path: '/api/v1/ping/', 
      description: 'Health check endpoint.',
      response: `{"success": true}`
    },
    { 
      title: 'List applications (legacy)', 
      method: 'GET', 
      path: '/api/v1/applications/', 
      description: 'Legacy endpoint for listing applications. Requires X-API-KEY header.',
      response: `[{"id": 1, "name": "app", "description": "..."}]`
    },
    { 
      title: 'Create application (legacy)', 
      method: 'POST', 
      path: '/api/v1/applications/', 
      description: 'Legacy endpoint for creating applications. Requires X-API-KEY header.',
      request: `{"name": "app", "description": "..."}`,
      response: `{"id": 1}`
    },
  ];

  const postgresEndpoints = [
    {
      title: 'PostgreSQL Status',
      method: 'GET',
      path: '/api/postgres/status/',
      description: 'Get PostgreSQL server version, uptime, database size, connection stats, and configuration parameters.',
      response: `{
  "success": true,
  "postgres": {
    "connected": true,
    "server_info": {"version": "15.4", "start_time": "...", "latency_ms": 1.2},
    "database": {"name": "registry", "size_bytes": 12345678, "size_pretty": "12 MB"},
    "connections": {"active": 2, "idle": 5, "total": 7, "max": 100},
    "tables": {"count": 15},
    "config": {"work_mem": "4MB", "shared_buffers": "128MB", ...}
  }
}`
    },
    {
      title: 'PostgreSQL Tables',
      method: 'GET',
      path: '/api/postgres/tables/',
      description: 'List tables with sizes, row counts, and vacuum stats. Supports ?page=, ?sort= (size|name|rows), ?search= parameters.',
      response: `{
  "success": true,
  "tables": [
    {"tablename": "registry_application", "size_pretty": "16 kB", "row_estimate": 5, "last_vacuum": "...", "last_analyze": "..."}
  ],
  "pagination": {"page": 1, "per_page": 25, "total": 10, "pages": 1}
}`
    },
    {
      title: 'PostgreSQL Activity',
      method: 'GET',
      path: '/api/postgres/activity/',
      description: 'Get current active queries and connections from pg_stat_activity.',
      response: `{
  "success": true,
  "activity": [
    {"pid": 1234, "usename": "registry", "state": "active", "query": "SELECT ...", "query_duration_secs": 0}
  ]
}`
    },
    {
      title: 'Execute SQL Query',
      method: 'POST',
      path: '/api/postgres/query/',
      description: 'Execute a read-only SQL query against the database. Only SELECT statements are allowed.',
      request: `{
  "sql": "SELECT * FROM registry_application LIMIT 10"
}`,
      response: `{
  "success": true,
  "columns": ["id", "name", "description", "is_active"],
  "rows": [
    [1, "api", "Core API Service", true]
  ],
  "row_count": 1
}`
    },
  ];

  const insightsEndpoints = [
    {
      title: 'Usage Analytics',
      method: 'GET',
      path: '/api/analytics/?days=30',
      description: 'Get usage analytics over a configurable time period (max 365 days). Includes audit log activity, version publications, application and version status breakdowns.',
      response: `{
  "period_days": 30,
  "audit_by_day": [{"date": "2026-02-01", "count": 5}],
  "audit_by_action": [{"action": "create", "count": 20}],
  "versions_by_day": [{"date": "2026-02-01", "count": 3}],
  "app_statuses": [{"status": "active", "count": 8}, {"status": "inactive", "count": 2}],
  "version_statuses": [{"status": "stable", "count": 15}, {"status": "pre-release", "count": 3}, {"status": "deprecated", "count": 1}],
  "total_applications": 10,
  "total_versions": 19
}`
    },
    {
      title: 'Health Check',
      method: 'GET',
      path: '/api/health/',
      description: 'Check internal health of the Registry service including database connectivity, application stats, version stats, and API key configuration.',
      response: `{
  "overall": "healthy",
  "checks": {
    "database": {"status": "healthy", "latency_ms": 1.23},
    "applications": {"status": "healthy", "total": 10, "active": 8},
    "versions": {"status": "healthy", "total": 19, "stable": 15, "deprecated": 1},
    "api_keys": {"status": "healthy", "total": 3, "active": 2}
  }
}`
    },
  ];

  const renderEndpoint = (endpoint) => (
    <AccordionItem key={endpoint.path + endpoint.method} border="none" mb={2}>
      <AccordionButton 
        bg="gray.50" 
        borderRadius="md" 
        _hover={{ bg: 'gray.100' }}
        _expanded={{ bg: 'gray.100', borderBottomRadius: 0 }}
      >
        <Flex flex="1" textAlign="left" align="center">
          <Badge colorScheme={methodColors[endpoint.method]} mr={3} fontSize="xs" px={2}>
            {endpoint.method}
          </Badge>
          <Code fontSize="sm" bg="transparent">{endpoint.path}</Code>
          <Text ml={4} color="gray.600" fontSize="sm">{endpoint.title}</Text>
        </Flex>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel pb={4} bg="white" borderWidth="1px" borderTop="0" borderBottomRadius="md">
        <Text color="gray.600" mb={4}>{endpoint.description}</Text>
        
        {endpoint.request && (
          <Box mb={4}>
            <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Request Body:</Text>
            <Code display="block" p={4} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">
              {endpoint.request}
            </Code>
          </Box>
        )}
        
        <Box>
          <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Response:</Text>
          <Code display="block" p={4} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">
            {endpoint.response}
          </Code>
        </Box>
      </AccordionPanel>
    </AccordionItem>
  );

  return (
    <Box>
      <Heading size="lg" mb={2}>API Documentation</Heading>
      <Text color="gray.600" mb={6}>
        Reference documentation for the Registry API. All endpoints require authentication via Token header.
      </Text>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
        <Heading size="md" mb={4}>Authentication</Heading>
        <Text color="gray.600" mb={4}>
          All API requests require authentication. Include your token in the Authorization header:
        </Text>
        <Code display="block" p={4} borderRadius="md" fontSize="sm" bg="gray.50">
          Authorization: Token your-token-here
        </Code>
        <Text color="gray.600" mt={4} fontSize="sm">
          For the legacy v1 API endpoints, use the X-API-KEY header with an API key instead.
        </Text>
      </Box>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
        <Heading size="md" mb={4}>Applications</Heading>
        <Text color="gray.600" mb={4}>
          Manage application registrations. Each application can have multiple versions.
        </Text>
        <Accordion allowMultiple>
          {applicationEndpoints.map(renderEndpoint)}
        </Accordion>
      </Box>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
        <Heading size="md" mb={4}>Versions</Heading>
        <Text color="gray.600" mb={4}>
          Manage versions for registered applications.
        </Text>
        <Accordion allowMultiple>
          {versionEndpoints.map(renderEndpoint)}
        </Accordion>
      </Box>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
        <Heading size="md" mb={4}>API Keys</Heading>
        <Text color="gray.600" mb={4}>
          Manage API keys for external access to the legacy v1 API.
        </Text>
        <Accordion allowMultiple>
          {apiKeyEndpoints.map(renderEndpoint)}
        </Accordion>
      </Box>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
        <Heading size="md" mb={4}>PostgreSQL Status</Heading>
        <Text color="gray.600" mb={4}>
          Monitor the PostgreSQL database — server status, table details, active queries, and run read-only SQL.
        </Text>
        <Accordion allowMultiple>
          {postgresEndpoints.map(renderEndpoint)}
        </Accordion>
      </Box>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
        <Heading size="md" mb={4}>Insights</Heading>
        <Text color="gray.600" mb={4}>
          Usage analytics and health check endpoints for monitoring the Registry service.
        </Text>
        <Accordion allowMultiple>
          {insightsEndpoints.map(renderEndpoint)}
        </Accordion>
      </Box>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
        <Heading size="md" mb={4}>Legacy API (v1)</Heading>
        <Text color="gray.600" mb={4}>
          These endpoints are maintained for backward compatibility. They require the X-API-KEY header.
        </Text>
        <Accordion allowMultiple>
          {legacyEndpoints.map(renderEndpoint)}
        </Accordion>
      </Box>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Heading size="md" mb={4}>Bash Aliases</Heading>
        <Text color="gray.600" mb={4}>
          A set of bash functions are available for interacting with the Registry API from the command line.
          Source the script to make these functions available in your terminal.
        </Text>
        
        <Box mb={4}>
          <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Setup:</Text>
          <Code display="block" p={4} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">
{`# Source the registry API script
source /path/to/selwyn/registry/tools/registry_api.sh

# Optional: Set custom environment variables
export REGISTRY_API_URL="http://registry.services.selwynevents.com"
export REGISTRY_API_KEY="your-api-key-here"`}
          </Code>
        </Box>

        <Box mb={4}>
          <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Available Functions:</Text>
          <Box bg="gray.50" borderRadius="md" p={4}>
            <Flex mb={3}>
              <Code bg="cyan.100" color="cyan.700" px={2} py={1} borderRadius="md" mr={3} minW="180px">list_applications</Code>
              <Text fontSize="sm" color="gray.600">List all registered applications</Text>
            </Flex>
            <Flex mb={3}>
              <Code bg="cyan.100" color="cyan.700" px={2} py={1} borderRadius="md" mr={3} minW="180px">get_application &lt;id&gt;</Code>
              <Text fontSize="sm" color="gray.600">Get details for a specific application by ID</Text>
            </Flex>
            <Flex mb={3}>
              <Code bg="cyan.100" color="cyan.700" px={2} py={1} borderRadius="md" mr={3} minW="180px">create_application &lt;name&gt; &lt;desc&gt;</Code>
              <Text fontSize="sm" color="gray.600">Create a new application with name and description</Text>
            </Flex>
            <Flex mb={3}>
              <Code bg="cyan.100" color="cyan.700" px={2} py={1} borderRadius="md" mr={3} minW="180px">get_versions &lt;app_id&gt;</Code>
              <Text fontSize="sm" color="gray.600">List all versions for an application</Text>
            </Flex>
            <Flex mb={3}>
              <Code bg="cyan.100" color="cyan.700" px={2} py={1} borderRadius="md" mr={3} minW="180px">get_version &lt;app_id&gt; &lt;ver_id&gt;</Code>
              <Text fontSize="sm" color="gray.600">Get a specific version by application ID and version ID</Text>
            </Flex>
            <Flex>
              <Code bg="cyan.100" color="cyan.700" px={2} py={1} borderRadius="md" mr={3} minW="180px">create_version &lt;app_id&gt; &lt;desc&gt;</Code>
              <Text fontSize="sm" color="gray.600">Create a new version for an application</Text>
            </Flex>
          </Box>
        </Box>

        <Box>
          <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={2}>Usage Examples:</Text>
          <Code display="block" p={4} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap" bg="gray.50">
{`# List all applications
list_applications

# Get details for application ID 1
get_application 1

# Create a new application
create_application "my-new-app" "A description of my application"

# Get all versions for application ID 1
get_versions 1

# Create a new version for application ID 1
create_version 1 "Version 2.0.0 with new features"`}
          </Code>
        </Box>

        <Box mt={4} p={4} bg="yellow.50" borderRadius="md" borderWidth="1px" borderColor="yellow.200">
          <Text fontSize="sm" color="yellow.800">
            <strong>Note:</strong> The script requires <Code fontSize="xs" bg="yellow.100">jq</Code> to be installed for JSON parsing.
            Install it with <Code fontSize="xs" bg="yellow.100">brew install jq</Code> (macOS) or <Code fontSize="xs" bg="yellow.100">apt-get install jq</Code> (Linux).
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default ApiDocs;
